import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions
} from '@tanstack/react-query';

import type {
  CatalogItemCardFragmentFragment,
  GetBannerAndTextQueryVariables,
  GetBarMenuProductsQuery,
  GetProductsQuery,
  GetProductsQueryVariables
} from '@schema/cart';

import API from '@api';
import { getURLProps } from '@utils/setPagesUrl';
import { withSsrCache } from '../../ssrCache';

/**
 * Товары каталога /catalog/:id (страница CatalogCategory).
 *
 * В ключ входит всё, что определяет содержимое списка, включая xStoreId и
 * addressId: они не передаются на сервер (уходят через cart token), но их
 * смена означает другой ответ — новый ключ форсирует перезапрос (раньше это
 * делала связка mobx-reaction + intent-координатор в useCatalogProducts).
 */
export type CatalogProductsParamsT = {
  /** Слаг рубрики из URL (/catalog/:id) */
  slug: string;
  /** Значение /type/N из URL */
  typeUrlID?: string;
  /** Активная группа (/group/x) */
  group?: string;
  /** Сырое значение ?groups= */
  groups?: string;
  /** Спецфильтр (/x/yes), он же filter из getURLProps */
  specOffer?: string;
  setOrder: string;
  isExpress: boolean;
  /** settings.epp_catalog — товаров на страницу */
  epp?: number | null;
  xStoreId?: number | string | null;
  addressId?: number | string | null;
};

/** Порт построения переменных из старого getCatalogItems (без offset-ветки) */
export const buildGetProductsVariables = (
  params: CatalogProductsParamsT,
  page: number
): GetProductsQueryVariables => {
  const { slug, typeUrlID, groups, specOffer, setOrder, isExpress, epp } =
    params;

  // Легаси: при отсутствии /group/ берётся ПЕРВЫЙ СИМВОЛ ?groups= —
  // сохранено как было
  const group = params.group || groups?.[0] || undefined;

  const variables: GetProductsQueryVariables = {
    type: slug,
    setOrder,
    group: groups?.length ? undefined : group,
    groups:
      group && groups?.length
        ? [group, ...groups.split(',').filter(Boolean)]
        : undefined,
    page,
    specOffer,
    isExpress
  };

  if (epp) {
    variables.setLimit = epp;
  }

  if (
    slug === 'promo' ||
    slug === 'newbee' ||
    slug === 'hit' ||
    slug === 'natural'
  ) {
    variables.specOffer = slug;
    variables.specOfferFirst = true;
    if (typeUrlID !== '0') {
      variables.type = typeUrlID;
    } else {
      delete variables.type;
    }
  }

  return variables;
};

export const catalogProductsInfiniteQueryOptions = (
  params: CatalogProductsParamsT,
  initialPage = 1
) => {
  const cacheKey = ['catalogProducts', 'infinite', params];

  return withSsrCache(
    infiniteQueryOptions({
      queryKey: cacheKey,
      queryFn: ({ pageParam }) =>
        API.getProducts(buildGetProductsVariables(params, pageParam)).then(
          res => {
            const totalCount = res.getProducts.totalCount || 0;
            const limit = params.epp || 0;

            return {
              items: res.getProducts.catalogItems ?? [],
              total: totalCount,
              pagination: {
                total: limit ? Math.ceil(totalCount / limit) : 1,
                current: pageParam
              },
              // Полный ответ страницы: из pages[0] хук собирает шапку,
              // фильтры, меню и seo через mapCatalogResponse
              getProducts: res.getProducts
            };
          }
        ),
      initialPageParam: initialPage,
      getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
        // Пустая страница — дальше грузить нечего (сбой подсчёта на сервере)
        if (!lastPage.items.length) {
          return undefined;
        }

        // Из dev (fix catalog pages infinite loading bug): при заходе сразу
        // на ?page=N страницы 1..N-1 не загружались, и сравнение «загружено
        // < всего» оставалось верным до самого конца каталога — список
        // грузился бесконечно. Пропущенные страницы считаем по первому
        // pageParam.
        const skippedCount = params.epp
          ? ((allPageParams[0] ?? 1) - 1) * params.epp
          : 0;

        const loadedCount = allPages.reduce(
          (acc, pageData) => acc + pageData.items.length,
          0
        );

        return skippedCount + loadedCount < lastPage.total
          ? lastPageParam + 1
          : undefined;
      },
      placeholderData: keepPreviousData
    }),
    'product'
  );
};

/**
 * ЭТАП 2 — баннер/текст/крошки рубрики (GetBannerAndText) как react-query.
 * Раньше жил императивно в Catalog/component.getMainInfo (setState в эффекте)
 * — SSR не мог отдать хлебные крошки. xStoreId в ключе (не в variables):
 * смена магазина перезапрашивает — замена старой mobx-reaction.
 */
export type BannerAndTextKeyParamsT = GetBannerAndTextQueryVariables & {
  xStoreId?: number | string | null;
};

/** Извлечено 1:1 из Catalog/component.setOrder (СВОЯ маппа, не каталожная!) */
export const resolveBannerAndTextSort = (
  pathname: string,
  id: string,
  // после свежего dev настройка приходит из схемы как Maybe<string>
  catalogSorting: string | null | undefined
): string => {
  const { sort, by } = getURLProps({ pathname });

  const urlParseParam =
    by === 'date' && sort === 'desc'
      ? 'newest'
      : by === 'price' && sort === 'asc'
      ? 'priceUp'
      : by === 'price' && sort === 'desc'
      ? 'priceDown'
      : by === 'title' && sort === 'asc'
      ? 'alphabet'
      : by === 'popular' && sort === 'desc'
      ? 'popular'
      : null;

  if (urlParseParam) {
    return urlParseParam;
  }
  if (id === 'newbee') {
    return 'newest';
  }
  return catalogSorting || 'popular';
};

/** Зеркало построения params из getMainInfo (ветка с id) + xStoreId в ключ */
export const buildBannerAndTextKeyParams = (
  pathname: string,
  id: string,
  opts: {
    catalogSorting?: string | null;
    eppCatalog?: number | null;
    xStoreId?: number | string | null;
  }
): BannerAndTextKeyParamsT => {
  const urlProps = getURLProps({ pathname });

  const params: BannerAndTextKeyParamsT = {
    type: id,
    setOrder: resolveBannerAndTextSort(pathname, id, opts.catalogSorting),
    page: +urlProps.page,
    group: urlProps.group,
    specOffer: urlProps.filter,
    xStoreId: opts.xStoreId ?? null
  };

  if (opts.eppCatalog) {
    params.setLimit = opts.eppCatalog;
  }

  if (
    id === 'promo' ||
    id === 'newbee' ||
    id === 'hit' ||
    id === 'natural' ||
    id === 'delikateska'
  ) {
    params.specOffer = id;
    params.specOfferFirst = true;

    if (urlProps.type !== '0') {
      params.type = urlProps.type;
    } else {
      delete params.type;
    }
  }

  return params;
};

export const bannerAndTextQueryOptions = (params: BannerAndTextKeyParamsT) =>
  withSsrCache(
    queryOptions({
      queryKey: ['bannerAndText', params],
      queryFn: () => {
        const { xStoreId: _xStoreId, ...variables } = params;
        return API.getBannerAndText(variables);
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData
    }),
    // Профиль стандартный, а не товарный: в ответе баннер, текст и крошки
    // рубрики — цен там нет. Магазин в ключе назван (`xStoreId` в объекте
    // params), так что персональный вариант отсекается автоматикой.
    'standard'
  );

export const CATALOG_FAQ_QUERY_KEY = 'catalogFaq';

export const catalogFaqQueryOptions = (url: string) =>
  withSsrCache(
    queryOptions({
      queryKey: [CATALOG_FAQ_QUERY_KEY, url],
      queryFn: () =>
        API.queryCatalogFaq({ url }).then(res => res?.queryCatalogFaq ?? []),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000
    }),
    'standard'
  );

export const BAR_MENU_PRODUCTS_LIMIT = 20;

/**
 * Товары категории бар-меню (/bar-menu/:barId/:tableId/categories/:type).
 * Магазин определяется menuStoreId (id бара из URL), поэтому xStoreId
 * в ключе не нужен.
 */
export const barMenuProductsInfiniteQueryOptions = (params: {
  menuStoreId: number;
  type: string;
  groups: string[];
}) =>
  infiniteQueryOptions({
    queryKey: [
      'barMenuProducts',
      'infinite',
      params.menuStoreId,
      params.type,
      params.groups
    ],
    queryFn: ({ pageParam }) =>
      API.getProducts({
        menuStoreId: params.menuStoreId,
        setOrder: 'popular',
        page: pageParam,
        setLimit: BAR_MENU_PRODUCTS_LIMIT,
        type: params.type,
        groups: params.groups.length ? params.groups : undefined
      }).then(res => ({
        items: res.getProducts.catalogItems ?? [],
        total: res.getProducts.totalCount || 0,
        pagination: {
          total: Math.ceil(
            (res.getProducts.totalCount || 0) / BAR_MENU_PRODUCTS_LIMIT
          ),
          current: pageParam
        },
        // Полный ответ страницы: из pages[0] компонент собирает
        // табы рубрик и фильтры групп
        getProducts: res.getProducts
      })),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (!lastPage.items.length) {
        return undefined;
      }

      const loadedCount = allPages.reduce(
        (acc, pageData) => acc + pageData.items.length,
        0
      );

      return loadedCount < lastPage.total ? lastPageParam + 1 : undefined;
    },
    placeholderData: keepPreviousData
  });

/**
 * Товары поставщика (/supplier/:id). xStoreId в ключе: на сервер он не
 * передаётся (уходит через cart token), но цены и наличие зависят от
 * магазина.
 *
 * ЭТАП 2 (SSR): сплошной keepPreviousData тут нельзя — при смене поставщика
 * показались бы товары предыдущего. Но после гидрации ключ меняется
 * {xStoreId: null} → {xStoreId: N} у ТОГО ЖЕ поставщика, и без плейсхолдера
 * страница на мгновение уходила в баннер «Пожалуйста, подождите» и
 * перерисовывала товары заново. Поэтому держим прошлые данные точечно —
 * только когда supplierId не изменился.
 */
export const productsBySupplierInfiniteQueryOptions = (params: {
  supplierId: number;
  setLimit: number;
  xStoreId?: number | string | null;
}) =>
  withSsrCache(
    infiniteQueryOptions({
      queryKey: [
        'productsBySupplier',
        'infinite',
        params.supplierId,
        params.setLimit,
        params.xStoreId ?? null
      ],
      queryFn: ({ pageParam }) =>
        API.getProductsBySupplierId({
          supplierId: params.supplierId,
          page: pageParam,
          setLimit: params.setLimit
        }).then(res => ({
          items: res?.getProductsBySupplierId?.catalogItem ?? [],
          total: res?.getProductsBySupplierId?.totalCount || 0
        })),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        if (!lastPage.items.length) {
          return undefined;
        }

        const loadedCount = allPages.reduce(
          (acc, pageData) => acc + pageData.items.length,
          0
        );

        return loadedCount < lastPage.total ? lastPageParam + 1 : undefined;
      },
      placeholderData: (previousData, previousQuery) => {
        const previousSupplierId = (
          previousQuery?.queryKey as unknown[] | undefined
        )?.[2];
        return previousSupplierId === params.supplierId
          ? previousData
          : undefined;
      }
    }),
    'product',
    [params.xStoreId]
  );

/**
 * Статичные подборки товаров по параметрам getProducts (BusinessPage и т.п.).
 * Без staleTime: как и раньше, каждый маунт запрашивает свежие цены/наличие,
 * кэш дедуплицирует только параллельные запросы. На КЛИЕНТЕ это не
 * изменилось: `withSsrCache` там no-op, серверная запись живёт 30–60 секунд.
 */
export const productsQueryOptions = (
  params: Parameters<typeof API.getProducts>[0]
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['products', params],
      queryFn: () => API.getProducts(params),
      select: (res: GetProductsQuery) => res?.getProducts?.catalogItems ?? []
    }),
    'product'
  );

/** Рубрика бар-меню на страницах баров; общая фабрика слайдера и SSR-реестра. */
export const barsRubricProductsQueryOptions = (
  type: string,
  menuStoreId?: number
) =>
  productsQueryOptions({
    type,
    setOrder: 'popular',
    setLimit: BAR_MENU_PRODUCTS_LIMIT,
    menuStoreId
  });

/**
 * Две товарные подборки на странице «Бизнесу» (/opt). Параметры целиком
 * входят в ключ (['products', params]), а в компоненте лежали инлайновыми
 * литералами — SSR-префетчу пришлось бы повторять их руками, и любая правка
 * молча ломала бы попадание в кэш. Общие фабрики, как на /faq и /cook.
 */
const BUSINESS_PRODUCTS_PARAMS = {
  page: 1,
  setLimit: 30,
  setOrder: 'rating'
} as const;

export const businessGiftSetsQueryOptions = () =>
  productsQueryOptions({
    ...BUSINESS_PRODUCTS_PARAMS,
    type: 'podarochnye-nabory-k-prazdniku'
  });

export const businessCertificatesQueryOptions = () =>
  productsQueryOptions({
    ...BUSINESS_PRODUCTS_PARAMS,
    type: 'podarochnye-sertifikaty'
  });

type BarMenuRubric = NonNullable<
  GetProductsQuery['getProducts']['catalogItems']
>[number]['mainBarRubric'];

export type BarMenuRubricWithProducts = BarMenuRubric & {
  products: CatalogItemCardFragmentFragment[];
};

// module-level: стабильная ссылка, группировка не пересчитывается на каждый рендер
const selectBarMenuMenuProducts = (res: GetBarMenuProductsQuery) => {
  const products = res.getProducts.catalogItems || [];

  const sortedRubricsArr = (
    products?.reduce((acc, item) => {
      const isInArr = acc.findIndex(
        (i: BarMenuRubric) => i?.id === item.mainBarRubric?.id
      );
      if (isInArr >= 0) {
        return acc;
      }
      return [...acc, item.mainBarRubric];
    }, []) as unknown as BarMenuRubric[]
  )?.sort((a, b) => Number(a?.weight) - Number(b?.weight));

  const rubricsWithSortedProducts = sortedRubricsArr?.map(item => {
    const productsInRubric = products
      .filter(i => i.mainBarRubric?.id === item?.id)
      ?.map(i => ({
        ...i,
        title: i.title
          ?.replace(
            /[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g,
            ' '
          )
          ?.replace(/&nbsp;/gi, ' ')
          ?.replace('\u00A0', ' ')
      }));
    return {
      ...item,
      products: productsInRubric
    };
  });

  return {
    rubricsWithProducts: (rubricsWithSortedProducts ||
      []) as unknown as BarMenuRubricWithProducts[],
    has18plusProducts: !!products?.find(i => !!i.is_18plus)
  };
};

const barMenuAllProductsQueryOptions = (menuStoreId: number) =>
  queryOptions({
    queryKey: ['barMenuMenuProducts', menuStoreId],
    queryFn: () => API.getBarMenuProducts({ menuStoreId, setLimit: 1000 }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });

/** Печатное меню бара (BarMenuMenu): все товары одним запросом, по рубрикам */
export const barMenuMenuProductsQueryOptions = (menuStoreId: number) =>
  queryOptions({
    ...barMenuAllProductsQueryOptions(menuStoreId),
    select: selectBarMenuMenuProducts
  });

const selectBarMenuCatalogItems = (res: GetBarMenuProductsQuery) =>
  res.getProducts.catalogItems ?? [];

export const barMenuCatalogItemsQueryOptions = (menuStoreId: number) =>
  queryOptions({
    ...barMenuAllProductsQueryOptions(menuStoreId),
    select: selectBarMenuCatalogItems
  });

export const barMenuProductsByIdsQueryOptions = (
  menuStoreId: number,
  ids: number[]
) =>
  queryOptions({
    ...barMenuAllProductsQueryOptions(menuStoreId),
    select: (res: GetBarMenuProductsQuery) => {
      const byId = new Map<number, CatalogItemCardFragmentFragment>(
        (res.getProducts.catalogItems ?? []).map(item => [
          Number(item.id),
          item
        ])
      );

      return ids
        .map(id => byId.get(id))
        .filter((item): item is CatalogItemCardFragmentFragment => !!item);
    }
  });

/**
 * Полная карточка товара (getProductInfoTemp): BarMenuProduct, позже
 * ProductPage. Ответ хранится сырым — prepareProductRes зависит от pathname
 * и вызывается на стороне компонента.
 *
 * Главный адресат товарного кэша: рассылка со ссылкой на товар приводит всю
 * базу подписчиков в одну карточку за несколько минут. `xStoreId` в ключе
 * ПОЗИЦИОННЫЙ, автоматика его не разберёт — передаём третьим аргументом.
 */
export const PRODUCT_INFO_TEMP_QUERY_KEY = 'productInfoTemp';

export const productInfoTempQueryOptions = (params: {
  id: number;
  isMenu?: boolean;
  xStoreId?: number | string | null;
  menuStoreId?: number | null;
  cartToken?: string | null;
}) =>
  withSsrCache(
    queryOptions({
      queryKey: [
        PRODUCT_INFO_TEMP_QUERY_KEY,
        params.id,
        !!params.isMenu,
        params.xStoreId ?? null,
        params.menuStoreId ?? null,
        params.cartToken ?? null
      ],
      queryFn: () =>
        API.getProductInfoTemp({
          id: params.id,
          isMenu: !!params.isMenu,
          menuStoreId: params.menuStoreId ?? undefined,
          cartToken: params.cartToken ?? undefined
        }),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000
    }),
    'product',
    // ЛИЧНЫЕ ЗНАЧЕНИЯ — ВЫКЛЮЧАТЕЛЬ СЕРВЕРНОГО КЭША. `cartToken` свой у
    // каждого посетителя: без него в этом списке ответ одного покупателя
    // лёг бы в Redis и достался бы следующему. `menuStoreId` — по той же
    // причине, что и `xStoreId`: цены и наличие зависят от магазина.
    [params.xStoreId, params.menuStoreId, params.cartToken]
  );

/**
 * Быстрый просмотр товара (модалка на карточке). xStoreId в ключе:
 * цены и наличие зависят от выбранного магазина.
 */
export const PRODUCT_QUICK_INFO_QUERY_KEY = 'productQuickInfo';

export const productQuickInfoQueryOptions = (
  catalogId: number,
  xStoreId?: number | string | null
) =>
  queryOptions({
    queryKey: [PRODUCT_QUICK_INFO_QUERY_KEY, catalogId, xStoreId ?? null],
    queryFn: () => API.getProductQuickInfo({ catalogId }),
    select: res => res.getProductByIdTemp,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });

/**
 * Товары по списку id (модалка задания и т.п.). xStoreId входит в ключ:
 * цены и наличие зависят от магазина (раньше перезапрос делала mobx-reaction).
 */
export const productListByIdsQueryOptions = (
  ids: number[],
  xStoreId?: number | string | null
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['productListByIds', ids, xStoreId ?? null],
      queryFn: () => API.getProductList({ ids }),
      select: res => res.getProductsListByIds?.catalogItems ?? [],
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData
    }),
    'product',
    [xStoreId]
  );
/**
 * Блок «Авторское меню» (AuthorMenu без type='main-hits'): список id целиком
 * входит в ключ `['productListByIds', ids, xStoreId]`.
 *
 * xStoreId не передаём: на первом рендере он pristine-null и у компонента.
 */
export const OYSTER_BAR_CHEF_PRODUCT_IDS = [
  10358, 10360, 13557, 81490, 81909, 84856, 84854, 81494, 84859
];

export const oysterBarChefMenuQueryOptions = () =>
  productListByIdsQueryOptions(OYSTER_BAR_CHEF_PRODUCT_IDS);
